import { useRef, useState, type FormEvent } from "react";
import { API_BASE_URL } from "@/lib/api/client";
import { useI18n } from "@/i18n";

interface QualityGroup {
  group_id: string;
  template_id: string;
  version: string;
  window_start: string;
  window_end: string;
  like: number;
  dislike: number;
  total: number;
  dislike_rate: number;
  sample_support: string;
  confidence: string;
  themes: Array<{ label: string; feedback_ids: string[] }>;
  evidence: Array<{ id: string; run_id: string; topic_id: string; attribution_event_id: string; feedback_note: string | null }>;
  observation_event_id: string;
  belief_revision_id: string;
}

interface QualityView {
  status: "unknown" | "pending" | "failed" | "completed" | "no_samples";
  error_code: string | null;
  attributed: number | null;
  unattributed: number | null;
  groups: QualityGroup[];
}

export function FeedbackQualityReview() {
  const { t } = useI18n();
  const [user, setUser] = useState("");
  const [agent, setAgent] = useState("soulmate");
  const [data, setData] = useState<QualityView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const generation = useRef(0);
  const label = (key: string) => t(`messageFeedbackPage.quality${key}`);
  const invalidate = () => {
    generation.current += 1;
    setData(null);
    setError(null);
    setLoading(false);
  };
  const load = async (event: FormEvent) => {
    event.preventDefault();
    const request = ++generation.current;
    setData(null);
    setError(null);
    setLoading(true);
    try {
      const params = new URLSearchParams({ user_id: user.trim(), agent_type: agent.trim() });
      const response = await fetch(`${API_BASE_URL}/v1/admin/message-feedback/quality?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const value: QualityView = await response.json();
      if (request === generation.current) setData(value);
    } catch (err) {
      if (request === generation.current) setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (request === generation.current) setLoading(false);
    }
  };
  const statusKeys = { unknown: "Unknown", pending: "Pending", failed: "Failed", completed: "Completed", no_samples: "NoSamples" };
  return (
    <section className="mb-6 rounded-lg border bg-white p-4" aria-label={label("Title")}>
      <h2 className="text-lg font-semibold">{label("Title")}</h2>
      <p className="my-2 text-sm text-gray-600">{label("Help")}</p>
      <form onSubmit={load} className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">{label("User")}<input required maxLength={255} className="block rounded border p-2" value={user} onChange={(event) => { invalidate(); setUser(event.target.value); }} /></label>
        <label className="text-sm">{label("Agent")}<input required maxLength={64} className="block rounded border p-2" value={agent} onChange={(event) => { invalidate(); setAgent(event.target.value); }} /></label>
        <button type="submit" disabled={loading || !user.trim() || !agent.trim()} className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50">{loading ? label("Loading") : label("Load")}</button>
      </form>
      {error && <p role="alert" className="text-red-700">{error}</p>}
      {data && <div className="space-y-3">
        <p role="status">{label("Status")}: {label(statusKeys[data.status])} {data.error_code || ""} · {label("Coverage")}: {data.attributed ?? label("Unknown")} / {data.unattributed ?? label("Unknown")}</p>
        {data.status === "no_samples" && data.groups.length === 0 && <p>{label("Empty")}</p>}
        {data.groups.map((group) => <article key={group.group_id} className="rounded border p-3 text-sm">
          <h3 className="break-all font-semibold">{group.template_id} · {group.version}</h3>
          <p className="my-2">[{group.window_start}, {group.window_end}) UTC</p>
          <p>{label("Votes")}: {group.like} / {group.dislike} / {group.total} / {(group.dislike_rate * 100).toFixed(1)}%</p>
          <p>{label("Support")}: {group.sample_support} / {group.confidence}</p>
          <ul className="my-2 list-inside list-disc">{group.themes.map((theme, index) => <li key={index}>{theme.label} ({theme.feedback_ids.length})<span className="block break-all text-xs text-gray-500">{label("Evidence")}: {theme.feedback_ids.join(", ")}</span></li>)}</ul>
          <details><summary className="cursor-pointer">{label("Evidence")}</summary>
            <p className="break-all">{group.observation_event_id} · {group.belief_revision_id}</p>
            <ul>{group.evidence.map((vote) => <li className="my-2 break-all" key={vote.id}>{vote.id} · {vote.run_id} · {vote.topic_id} · {vote.attribution_event_id}<p className="whitespace-pre-wrap">{vote.feedback_note}</p></li>)}</ul>
          </details>
        </article>)}
      </div>}
    </section>
  );
}
